import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Send, Loader2, Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { useMeetingLock } from '../contexts/MeetingLockContext';
import PageHeader from '../components/PageHeader';

function BotChatPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { active: meetingLocked } = useMeetingLock();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const botName = user?.bot_name || 'Tudor';

  useEffect(() => { fetchHistory(); }, []);
  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const fetchHistory = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/bot/history`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const sendMessage = async (text) => {
    const msg = text || newMessage;
    if (!msg.trim()) return;
    setNewMessage('');
    setIsLoading(true);

    if (inputRef.current) {
      inputRef.current.style.height = '48px';
    }

    setMessages(prev => [...prev, { role: 'user', content: msg.trim(), timestamp: new Date().toISOString() }]);

    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/bot/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: new Date().toISOString() }]);
      } else {
        toast.error('Failed to get response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('An error occurred');
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startRecording = async () => {
    if (meetingLocked) {
      toast.warning('Microphone is locked while a meeting is in progress.  Take your After selfie in the Meeting Log to release the lock.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAndSend(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= 120) {
            stopRecording();
            return 120;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Mic error:', error);
      toast.error('Microphone access denied. Please allow microphone in your browser settings.');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const transcribeAndSend = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/transcribe`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.text && data.text.trim()) {
          await sendMessage(data.text.trim());
        } else {
          toast.error('Could not understand audio. Try again or type your message.');
        }
      } else {
        toast.error('Transcription failed');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Voice processing failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="min-h-screen bg-zinc-100">
      <PageHeader title={`Chat with ${botName}`} />

      {/* Disclosure line: strict Tudor-is-AI reminder (ARP follow-up, Feb 2026).
          Must appear at the TOP of the chat screen, always, before any messages. */}
      <div className="fixed top-14 left-0 right-0 bg-white border-b-2 border-black z-30" data-testid="sponsor-bot-disclosure">
        <div className="max-w-lg mx-auto px-4 py-2">
          <p className="text-sm text-black leading-snug">
            <strong>{botName}</strong> is an AI sponsor. It is not a person, a clinician or a crisis
            service. If you are unsafe, tap <button
              type="button"
              onClick={() => navigate('/get-help')}
              className="font-bold underline text-red-600 hover:text-red-700"
              data-testid="sponsor-bot-get-help-link"
            >Get help now</button>.
          </p>
        </div>
      </div>

      {/* Messages area: scrollable, with padding for fixed input bar */}
      <div className="pt-28 pb-32 overflow-y-auto" style={{ minHeight: 'calc(100vh - 56px)' }}>
        <div className="max-w-lg mx-auto px-4 py-4 min-h-full flex flex-col">
          {/* Welcome message: always shows at top */}
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center mb-3">
                <span className="text-xl font-bold text-white">{botName.charAt(0)}</span>
              </div>
              <h2 className="text-base font-bold text-zinc-900 mb-1">{botName}</h2>
              <p className="text-xs text-zinc-500 text-center max-w-xs mb-2">
                Your 24/7 Recovery sponsor. I'm here whenever you need to talk.
              </p>
              <p className="text-xs text-zinc-400 text-center">Type a message below or tap the mic to talk.</p>
            </div>
          )}

          {/* Chat messages */}
          {messages.map((message, index) => (
            <div
              key={index}
              className={`mb-3 flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-white border border-zinc-200 text-zinc-800 rounded-bl-sm shadow-sm'
                }`}
                data-testid={`chat-message-${index}`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                <p className={`text-[10px] mt-1 ${message.role === 'user' ? 'text-blue-200' : 'text-zinc-400'}`}>
                  {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {(isLoading || isTranscribing) && (
            <div className="mb-3 flex justify-start">
              <div className="bg-white border border-zinc-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-[10px] text-zinc-400">{isTranscribing ? 'Processing voice...' : `${botName} is typing...`}</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* INPUT BAR: FIXED to bottom of screen, always visible */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-zinc-300 px-3 py-3 pb-8 shadow-2xl z-40" data-testid="chat-input-bar">
        {/* Recording indicator */}
        {isRecording && (
          <button
            onClick={stopRecording}
            className="w-full flex items-center justify-center gap-2 py-2.5 mb-2 bg-red-600 text-white rounded-xl animate-pulse"
            data-testid="stop-voice-btn"
          >
            <MicOff className="w-4 h-4" />
            <span className="text-xs font-bold">Listening... {formatTime(recordingTime)}: Tap to Send</span>
          </button>
        )}

        <div className="max-w-lg mx-auto flex items-end gap-2">
          {/* Mic button: ALWAYS visible */}
          {!isRecording && (
            <button
              onClick={startRecording}
              disabled={isLoading || isTranscribing}
              className="h-12 w-12 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white flex-shrink-0 flex items-center justify-center disabled:opacity-50 transition-colors shadow-md"
              data-testid="voice-input-btn"
              title="Tap to talk"
            >
              {isTranscribing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
            </button>
          )}

          {/* Text input: DARK BORDER, always visible */}
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              rows={1}
              autoFocus
              disabled={isLoading || isRecording || isTranscribing}
              className="w-full resize-none rounded-xl border-2 border-zinc-900 bg-white px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 disabled:opacity-50 shadow-sm"
              style={{ minHeight: '48px', maxHeight: '120px' }}
              onInput={(e) => {
                e.target.style.height = '48px';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              data-testid="bot-message-input"
            />
          </div>

          {/* Send button */}
          <Button
            onClick={() => sendMessage()}
            disabled={isLoading || !newMessage.trim() || isRecording || isTranscribing}
            className="h-12 w-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0 p-0 shadow-md"
            data-testid="send-bot-message-btn"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default BotChatPage;
